import React from 'react';
import { Check, Users, PawPrint, Sparkles } from 'lucide-react';
import { accommodations } from '../data/mock';

export const Accommodation = () => {
  return (
    <section id="accommodation" className="py-24 bg-gradient-to-b from-white to-emerald-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 rounded-full px-4 py-2 mb-4">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Our Accommodation</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Stay
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From luxury shepherd's huts to private camping pitches, we have the perfect spot for your mountain escape.
          </p>
        </div>

        {/* Accommodation Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {accommodations.map((accommodation) => (
            <div
              key={accommodation.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src={accommodation.image}
                  alt={accommodation.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                  {accommodation.quantity} Available
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {accommodation.name}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {accommodation.description}
                </p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {accommodation.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* On-Site Facilities (for shepherd's huts) */}
                {accommodation.facilities && (
                  <div className="mb-6 bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                    <h4 className="font-semibold text-emerald-900 mb-3 text-sm">Available On-Site:</h4>
                    <div className="space-y-2">
                      {accommodation.facilities.map((facility, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-emerald-800">{facility}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Capacity */}
                <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      Up to {accommodation.capacity} {accommodation.capacity === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <PawPrint className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-600">Dog friendly</span>
                  </div>
                </div>

                {/* Pricing */}
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">
                      £{accommodation.price}
                      <span className="text-lg font-normal text-gray-600">/night</span>
                    </div>
                    {accommodation.singleNightPrice && (
                      <div className="text-sm text-gray-500 mt-1">
                        £{accommodation.singleNightPrice} for single night
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <p className="text-amber-900 font-medium">
              <PawPrint className="w-5 h-5 inline-block mr-2" />
              All our accommodation is dog-friendly at no extra charge. Each shepherd's hut includes a private field for your furry friends!
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <p className="text-blue-900 font-medium">
              <Users className="w-5 h-5 inline-block mr-2" />
              Our camping pitches are perfect for families! Safe outdoor spaces for children to play and explore the Welsh countryside.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
